import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { auth, onAuthStateChanged } from '../auth';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import Alert from '../components/ui/Alert';
import { acceptChallengeViaTicket, passChallengeTicket, previewChallengeTicket, TICKET_PREVIEW_TIMEOUT_MS } from '../api/challengeTicketService';
import { fetchUserProfile, hasRealProfile as profileIsComplete } from '../api/userProfileService';
import {
  capturePendingTicketFromPath,
  setPendingTicketCode,
  clearPendingTicketIntent,
  logTicketResume,
} from '../utils/challengeTicketIntent';
import {
  resolveTicketPageState,
  canonicalTicketState,
  TICKET_PAGE_STATE,
} from '../utils/challengeTicketPageStates';
import { TicketStatus } from '../utils/ticketStatus';
import {
  formatTicketExpiryRemaining,
  ticketSenderLine,
  ticketMatchupLabel,
  hasTicketTerminalRecap,
} from '../utils/ticketRecipientView';
import { forceDismissBootSplash } from '../pwa/bootSplash';
import { markTicketAppMounted } from '../pwa/ticketDeepLinkRecovery';
import { mapPassChallengeTicketError } from '../utils/challengeTicketPassErrors';
import { typography } from '../design-system/tokens';
import SportIcon from '../components/common/SportIcon';
import { getSportLabel } from '../constants/sportTheme';

/** Open-ticket states where recipient sees matchup, sender, and expiry (PREVIEW is unreachable at runtime). */
const OPEN_TICKET_RECIPIENT_STATES = [
  TICKET_PAGE_STATE.SIGN_IN,
  TICKET_PAGE_STATE.COMPLETE_PROFILE,
  TICKET_PAGE_STATE.ACCEPT_READY,
  TICKET_PAGE_STATE.ACCEPTING,
];

const TERMINAL_TICKET_STATES = [
  TICKET_PAGE_STATE.EXPIRED,
  TICKET_PAGE_STATE.CANCELED,
  TICKET_PAGE_STATE.ALREADY_ACCEPTED,
  TICKET_PAGE_STATE.PASSED,
];

function parseTicketCode(pathname) {
  const match = String(pathname || '').match(/^\/ticket\/([^/?#]+)/);
  return match ? decodeURIComponent(match[1]).toUpperCase() : null;
}

function copyTicketLink() {
  const url = typeof window !== 'undefined' ? window.location.href : '';
  if (!url) return;
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(url).catch(() => {
      window.prompt('Copy this ticket link:', url);
    });
    return;
  }
  window.prompt('Copy this ticket link:', url);
}
function formatMoney(amount) {
  const n = Number(amount);
  if (Number.isNaN(n)) return '—';
  return `$${n.toFixed(0)}`;
}

function TicketStatusCard({ title, body, tone = 'warning', children }) {
  return (
    <div className="space-y-4">
      <Alert tone={tone} className="text-sm font-bold">{title}</Alert>
      {body && <p className="text-sm text-gray-400 text-center font-medium">{body}</p>}
      {children}
    </div>
  );
}

function TicketSportHeader({ sport }) {
  const label = sport ? getSportLabel(sport) : 'Game lock';
  return (
    <p className={`${typography.microWide} text-gray-500 inline-flex items-center gap-1.5`}>
      {sport && <SportIcon sport={sport} size="xs" decorative />}
      <span>{label}</span>
    </p>
  );
}

function TicketMatchupRecap({ preview }) {
  if (!hasTicketTerminalRecap(preview)) return null;
  return (
    <Card variant="glass" className="p-5 space-y-3">
      <TicketSportHeader sport={preview.sport} />
      <p className="text-xl font-black text-white">{ticketMatchupLabel(preview)}</p>
      <p className="text-2xl font-black text-emerald-400 tabular-nums">
        {formatMoney(preview.amount)}
        <span className="text-sm text-gray-500 font-bold ml-2">per player</span>
      </p>
      {preview.creatorDisplayName && (
        <p className="text-sm text-gray-400 font-medium">
          From {preview.creatorDisplayName}
        </p>
      )}
    </Card>
  );
}

export default function ChallengeTicketPage({ onEnterLockbox }) {
  const location = useLocation();
  const navigate = useNavigate();
  const ticketCode = useMemo(() => parseTicketCode(location.pathname), [location.pathname]);

  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(undefined);
  const [authReady, setAuthReady] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [passing, setPassing] = useState(false);
  const [passComplete, setPassComplete] = useState(null);
  const [error, setError] = useState('');
  const [loadAttempt, setLoadAttempt] = useState(0);

  useEffect(() => {
    console.log('[ticket] mounted');
    markTicketAppMounted();
    forceDismissBootSplash();
  }, []);

  useEffect(() => {
    console.log('[ticket] code parsed', ticketCode ? 'ok' : 'missing');
  }, [ticketCode]);

  useEffect(() => {
    return onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);
      setAuthReady(true);
      if (firebaseUser?.uid) {
        setProfileLoading(true);
        try {
          setProfile(await fetchUserProfile(firebaseUser.uid));
        } catch (_) {
          // Treat a failed profile fetch as "no profile yet" rather than leaving
          // it undefined forever, so the CTA resolves instead of hanging.
          setProfile(null);
        } finally {
          setProfileLoading(false);
        }
      } else {
        setProfile(null);
        setProfileLoading(false);
      }
    });
  }, []);

  const loadPreview = useCallback(() => {
    if (!ticketCode) {
      setLoading(false);
      setError('Invalid challenge ticket link.');
      return () => {};
    }
    let cancelled = false;
    let timedOut = false;
    setLoading(true);
    setError('');
    const startedAt = Date.now();
    console.log('[ticket] preview request started');

    const timeoutId = window.setTimeout(() => {
      if (cancelled || timedOut) return;
      timedOut = true;
      console.log(`[ticket] preview timeout after ${TICKET_PREVIEW_TIMEOUT_MS}ms`);
      setLoading(false);
      setPreview(null);
      setError('Could not load ticket');
    }, TICKET_PREVIEW_TIMEOUT_MS);

    previewChallengeTicket(ticketCode)
      .then((data) => {
        if (cancelled || timedOut) return;
        console.log(`[ticket] preview success in ${Date.now() - startedAt}ms`, {
          ticketCode,
          status: data?.status,
          openable: data?.openable,
          reason: data?.reason,
        });
        setPreview(data);
      })
      .catch((err) => {
        if (cancelled || timedOut) return;
        const message = err?.status === 404
          ? 'Challenge ticket not found'
          : (err?.message || 'Could not load ticket');
        console.log(`[ticket] preview failure in ${Date.now() - startedAt}ms`, message);
        setPreview(null);
        setError(message);
        console.error('[ticket] page_failed', message);
      })
      .finally(() => {
        if (!cancelled && !timedOut) {
          window.clearTimeout(timeoutId);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [ticketCode]);

  useEffect(() => {
    if (ticketCode) capturePendingTicketFromPath(location.pathname);
  }, [ticketCode, location.pathname]);

  useEffect(() => loadPreview(), [loadPreview, loadAttempt]);

  useEffect(() => {
    if (loading || !ticketCode) return;
    if (!preview && !error) return;
    const isResume = new URLSearchParams(location.search).get('resume') === '1';
    if (!isResume) return;
    clearPendingTicketIntent('ticket screen loaded');
  }, [ticketCode, loading, preview, error, location.search]);

  const handleSignIn = () => {
    if (ticketCode) {
      setPendingTicketCode(ticketCode, { path: location.pathname.split('?')[0] });
      logTicketResume('auth required', ticketCode);
    }
    navigate('/signup');
  };

  const hasRealProfile = profileIsComplete(profile);
  const profileReady = profile !== undefined && hasRealProfile;

  const pageState = useMemo(
    () => resolveTicketPageState({
      loading,
      error,
      preview,
      user,
      profileReady,
      accepting,
      passing,
      passComplete,
      authReady,
      profileLoading,
    }),
    [loading, error, preview, user, profileReady, accepting, passing, passComplete, authReady, profileLoading],
  );

  useEffect(() => {
    console.log('[ticket] state', {
      state: pageState,
      canonical: canonicalTicketState(pageState),
      ticketCode,
      firebaseUid: user?.uid ?? null,
      authReady,
      hasRealProfile,
      profileLoading,
      ticketStatus: preview?.status ?? null,
    });
  }, [pageState, ticketCode, user?.uid, authReady, hasRealProfile, profileLoading, preview?.status]);

  const handleAccept = async () => {
    // Accept requires a settled, real Firebase identity. Guests (no firebase
    // user) and pre-auth states can never reach this mutation.
    if (!ticketCode) return;
    if (!authReady || !user?.uid || !auth.currentUser?.uid) {
      console.warn('[ticket] accept blocked: auth not ready', {
        authReady,
        firebaseUid: user?.uid ?? null,
      });
      return;
    }
    if (profileLoading) return;
    if (!hasRealProfile) {
      if (ticketCode) {
        setPendingTicketCode(ticketCode, { path: location.pathname.split('?')[0] });
        logTicketResume('profile required', ticketCode);
      }
      navigate('/create-profile', { replace: true });
      return;
    }
    setAccepting(true);
    setError('');
    console.log('[ticket] accept request', {
      ticketCode,
      firebaseUid: user.uid,
      ticketStatusBefore: preview?.status ?? null,
    });
    try {
      const result = await acceptChallengeViaTicket(ticketCode);
      console.log('[ticket] accept success', {
        ticketCode,
        firebaseUid: user.uid,
        challengeId: result?.challengeId ?? null,
        ticketStatusAfter: result?.status ?? TicketStatus.ACCEPTED,
      });
      clearPendingTicketIntent('accepted');
      onEnterLockbox?.();
      navigate('/app', { replace: true });
    } catch (err) {
      const message = err?.message || 'Could not accept challenge ticket';
      setError(message);
      console.error('[ticket] accept failed', message);
    } finally {
      setAccepting(false);
    }
  };

  const passLocked = passing || Boolean(passComplete);

  const handlePass = async () => {
    if (!ticketCode || passLocked) return;
    if (!authReady || !user?.uid || !auth.currentUser?.uid) {
      handleSignIn();
      return;
    }
    setPassing(true);
    setError('');
    try {
      const result = await passChallengeTicket(ticketCode);
      clearPendingTicketIntent('user passed');
      const terminal = Boolean(result?.terminal);
      setPassComplete({ terminal, targeted: Boolean(result?.targeted) });
      if (terminal) {
        setPreview((prev) => (prev ? {
          ...prev,
          status: TicketStatus.DECLINED,
          openable: false,
          reason: 'This challenge ticket was passed on.',
        } : prev));
      }
    } catch (err) {
      setError(mapPassChallengeTicketError(err));
    } finally {
      setPassing(false);
    }
  };

  const retryLoad = () => setLoadAttempt((n) => n + 1);

  if (!ticketCode) {
    return (
      <div className="min-h-screen min-h-[100dvh] bg-[#0a0a0a] text-white flex items-center justify-center px-4">
        <Card variant="solid" padding="lg" className="max-w-md w-full text-center space-y-4">
          <p className="text-lg font-black">Ticket not found</p>
          <p className="text-sm text-gray-400 font-medium">This challenge ticket link is invalid.</p>
          <Button type="button" variant="secondary" fullWidth onClick={() => { window.location.href = '/'; }}>
            Go Home
          </Button>
        </Card>
      </div>
    );
  }

  const matchupLabel = ticketMatchupLabel(preview);
  const senderLine = ticketSenderLine(preview);
  const expiryRemaining = formatTicketExpiryRemaining(preview);
  const showOpenRecipientContext = OPEN_TICKET_RECIPIENT_STATES.includes(pageState);
  const showTerminalRecap = TERMINAL_TICKET_STATES.includes(pageState);

  const headerTitle = (() => {
    switch (pageState) {
      case TICKET_PAGE_STATE.LOADING:
        return 'Loading ticket…';
      case TICKET_PAGE_STATE.NOT_FOUND:
        return 'Ticket not found';
      case TICKET_PAGE_STATE.EXPIRED:
        return 'Ticket expired';
      case TICKET_PAGE_STATE.CANCELED:
        return 'Ticket canceled';
      case TICKET_PAGE_STATE.ALREADY_ACCEPTED:
        return 'Already accepted';
      case TICKET_PAGE_STATE.PASSED:
        return 'You passed on this ticket';
      case TICKET_PAGE_STATE.PASSED_SHAREABLE:
        return 'You passed on this ticket';
      case TICKET_PAGE_STATE.COULD_NOT_LOAD:
        return 'Could not load ticket';
      default:
        return matchupLabel;
    }
  })();

  return (
    <div className="min-h-screen min-h-[100dvh] bg-[#0a0a0a] text-white px-4 py-10 sm:px-6">
      <div className="max-w-md mx-auto space-y-6">
        <header className="text-center space-y-2">
          <p className={`${typography.microWide} text-emerald-400`}>Challenge Ticket</p>
          <h1 className="text-2xl font-black">{headerTitle}</h1>
          {senderLine && showOpenRecipientContext && (
            <p className="text-sm text-gray-400 font-medium">{senderLine}</p>
          )}
          {expiryRemaining && showOpenRecipientContext && (
            <p className={`${typography.microWide} text-amber-400/90`}>
              {expiryRemaining}
            </p>
          )}
        </header>

        {pageState === TICKET_PAGE_STATE.LOADING && (
          <p className="text-sm text-gray-500 text-center font-medium">Loading ticket…</p>
        )}

        {pageState === TICKET_PAGE_STATE.COULD_NOT_LOAD && (
          <TicketStatusCard
            title="Could not load ticket"
            body={error || 'Check the link and try again.'}
          >
            <div className="flex flex-col gap-2">
              <Button type="button" variant="brand" fullWidth onClick={retryLoad}>Retry</Button>
              <Button type="button" variant="secondary" fullWidth onClick={() => { window.location.href = '/'; }}>
                Go Home
              </Button>
              <Button type="button" variant="ghost" fullWidth onClick={copyTicketLink}>
                Copy Link
              </Button>
            </div>
          </TicketStatusCard>
        )}

        {pageState === TICKET_PAGE_STATE.NOT_FOUND && (
          <TicketStatusCard title="This challenge ticket was not found." body="The link may be wrong or the ticket was removed.">
            <Button type="button" variant="secondary" fullWidth onClick={() => { window.location.href = '/'; }}>
              Go Home
            </Button>
          </TicketStatusCard>
        )}

        {showTerminalRecap && <TicketMatchupRecap preview={preview} />}

        {pageState === TICKET_PAGE_STATE.EXPIRED && (
          <TicketStatusCard
            title="This challenge ticket has expired."
            body="Ask the sender for a new ticket link, or start your own challenge from home."
          >
            <Button type="button" variant="secondary" fullWidth onClick={() => { window.location.href = '/'; }}>
              Go Home
            </Button>
          </TicketStatusCard>
        )}

        {pageState === TICKET_PAGE_STATE.CANCELED && (
          <TicketStatusCard
            title="This challenge ticket was canceled."
            body="The sender canceled before anyone accepted. Start a new challenge from home if you want to lock in again."
          >
            <Button type="button" variant="secondary" fullWidth onClick={() => { window.location.href = '/'; }}>
              Go Home
            </Button>
          </TicketStatusCard>
        )}

        {pageState === TICKET_PAGE_STATE.PASSED && (
          <TicketStatusCard
            title="You passed on this ticket."
            body="The sender has been notified. You can start a new challenge from home anytime."
            tone="success"
          >
            <Button type="button" variant="secondary" fullWidth onClick={() => { window.location.href = '/'; }}>
              Go Home
            </Button>
          </TicketStatusCard>
        )}

        {pageState === TICKET_PAGE_STATE.PASSED_SHAREABLE && (
          <TicketStatusCard
            title="You passed on this ticket."
            body="This link stays open for others to accept."
            tone="success"
          >
            <Button type="button" variant="secondary" fullWidth onClick={() => { window.location.href = '/'; }}>
              Go Home
            </Button>
          </TicketStatusCard>
        )}

        {pageState === TICKET_PAGE_STATE.ALREADY_ACCEPTED && (
          <TicketStatusCard
            title="This challenge ticket was already accepted."
            body="The challenge is active or complete. Open the app to see the latest status."
            tone="success"
          >
            {preview?.challengeId ? (
              <Button type="button" variant="brand" fullWidth onClick={() => navigate('/app')}>
                View Challenge
              </Button>
            ) : (
              <Button type="button" variant="secondary" fullWidth onClick={() => { window.location.href = '/'; }}>
                Go Home
              </Button>
            )}
          </TicketStatusCard>
        )}

        {preview && showOpenRecipientContext && (
          <Card variant="glass" className="p-5 space-y-3">
            <TicketSportHeader sport={preview.sport} />
            <p className="text-xl font-black text-white">{matchupLabel}</p>
            <p className="text-3xl font-black text-emerald-400 tabular-nums">
              {formatMoney(preview.amount)}
              <span className="text-sm text-gray-500 font-bold ml-2">per player</span>
            </p>
            <p className="text-[10px] uppercase tracking-widest text-gray-600">
              Accept to lock funds and start the challenge
            </p>
          </Card>
        )}

        {pageState === TICKET_PAGE_STATE.SIGN_IN && (
          <div className="space-y-3">
            <p className="text-sm text-gray-400 text-center font-medium">
              Create account or sign in to accept this ticket.
            </p>
            <Button type="button" variant="brand" fullWidth onClick={handleSignIn}>
              Create account or sign in
            </Button>
            <Button
              type="button"
              variant="ghost"
              fullWidth
              disabled={passLocked}
              onClick={handlePass}
            >
              {passing ? 'Passing…' : 'Pass on this ticket'}
            </Button>
            <p className="text-center text-[10px] text-gray-600 font-bold">
              Sign in required to pass · passing leaves the ticket open for others
            </p>
          </div>
        )}

        {pageState === TICKET_PAGE_STATE.COMPLETE_PROFILE && (
          <div className="space-y-3">
            {error && <Alert tone="error" className="text-sm font-bold">{error}</Alert>}
            <p className="text-sm text-gray-400 text-center">Complete your profile to accept.</p>
            <Button
              type="button"
              variant="brand"
              fullWidth
              onClick={() => {
                if (ticketCode) {
                  setPendingTicketCode(ticketCode, { path: location.pathname.split('?')[0] });
                  logTicketResume('profile required', ticketCode);
                }
                navigate('/create-profile');
              }}
            >
              Complete profile to accept
            </Button>
            <Button
              type="button"
              variant="ghost"
              fullWidth
              disabled={passLocked || accepting}
              onClick={handlePass}
            >
              {passing ? 'Passing…' : 'Pass on this ticket'}
            </Button>
          </div>
        )}

        {(pageState === TICKET_PAGE_STATE.ACCEPT_READY || pageState === TICKET_PAGE_STATE.ACCEPTING) && (
          <div className="space-y-2">
            {error && <Alert tone="error" className="text-sm font-bold">{error}</Alert>}
            <Button
              type="button"
              variant="brand"
              fullWidth
              disabled={accepting || passLocked}
              onClick={handleAccept}
            >
              {accepting ? 'Accepting…' : 'Accept challenge'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              fullWidth
              disabled={passLocked || accepting}
              onClick={handlePass}
            >
              {passing ? 'Passing…' : 'Pass on this ticket'}
            </Button>
            <p className="text-center text-[10px] text-gray-600 font-bold">
              Passing leaves the ticket open for others · funds lock on accept
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
